// Recovery for stale lazy-chunk references after a deploy: a tab loaded before a
// deploy points at chunk files the new build no longer has -> 404 on navigation or
// dynamic import. A reload fetches the current bundles and self-heals (index.html is
// served no-cache and CF doesn't cache it).
//
// We do NOT reload automatically: a page silently reloading under the user is
// jarring. Instead we surface a persistent, non-blocking notification with a Reload
// action and let the user trigger it. The same failure also happens on a plain
// network blip (offline/flaky), so the copy stays cause-neutral. Used by both the
// router navigation-error handler (lazy routes) and the global ErrorHandler (other
// dynamic imports).

import type { MatSnackBarConfig } from '@angular/material/snack-bar';
import type { NotificationService } from '@rms/notification';

// Suppress duplicate prompts within this window so a burst of failing chunks (or
// repeated navigation attempts) doesn't re-fire the snackbar/translation lookup.
const CHUNK_PROMPT_COOLDOWN_MS = 10000;

// Persistent (no `duration`), top-center, error-styled snackbar so the prompt stays
// until the user acts on it instead of auto-dismissing after a few seconds.
const CHUNK_RELOAD_SNACKBAR_CONFIG: MatSnackBarConfig = {
  horizontalPosition: 'center',
  verticalPosition: 'top',
  panelClass: ['rounded', 'p-2', 'bg-ruber', 'text-flavius'],
};

let lastPromptAt = 0;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message ?? '';

  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk [\w-]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

// Shows a persistent notification asking the user to reload; reload happens only when
// the user clicks the action. Deduped within a short window so simultaneous chunk
// failures don't stack. No-op without a window.
export function promptReloadForFreshChunks(notification: NotificationService, win: Window | null): void {
  if (!win) {
    return;
  }

  const now = Date.now();

  if (now - lastPromptAt < CHUNK_PROMPT_COOLDOWN_MS) {
    return;
  }

  lastPromptAt = now;

  notification.notify('common.errors.content-load', 'common.reload', CHUNK_RELOAD_SNACKBAR_CONFIG, () =>
    win.location.reload()
  );
}
